'use client'

import { ArticleResponseVo } from '@schemas/non-auth'
import { Box } from '@mui/joy'
import { ParticleCSS } from './Particles/ParticleCSS'
import articlePartial from './Particles'
import { useEffect, useRef, useState } from 'react'
import pxToRem from '@utils/pxToRem'
import { useIsAppControl } from '@store/isApp'
import useIframeZoom from '@hooks/useIframeZoom'
import { theme } from '@ui/style'
import { createPortal } from 'react-dom'
import BizboardAdFit from '@components/ads/Article/Bizboard'
import { TextTopAd } from '@components/ads/Article/TextAds'
import BodyImgMoAd from '@components/ads/Article/Body/BodyImgMo'
import BodyVideoPcAd from '@components/ads/Article/Body/BodyVideoPc'

interface ArticleProps {
	article: ArticleResponseVo
}

const Article = ({ article }: ArticleProps) => {
	const { isApp } = useIsAppControl()
	const { iframeStyles } = useIframeZoom()
	const articleRef = useRef<HTMLDivElement>(null)
	const { articleContent, vodInfoList, articleIdx } = article
	const content = articleContent
		? articlePartial(articleContent, vodInfoList, articleIdx)
		: undefined

	const [textAd, setTextAd] = useState<Element | null>(null)
	const [bizboardAd, setBizboardAd] = useState<Element | null>(null)
	const [contentImageAd, setContentImageAd] = useState<Element | null>(null)
	const [contentVideoAd, setContentVideoAd] = useState<Element | null>(null)

	/** [광고] 본문 이미지 베너 AD - MW (PC아님) */
	useEffect(() => {
		const container = articleRef.current

		if (isApp || !container) {
			return
		}

		/** 광고 컨텐츠 셋팅 */
		const handleContentAd = (type: 'video' | 'image', ele: Element | ChildNode | null) => {
			if (!ele) {
				return
			}

			const setContentAd = (ad: Element) => {
				if (type === 'video') {
					setContentVideoAd(ad)
					return
				}

				setContentImageAd(ad)
			}

			const className = type === 'video' ? 'set_contents_video_ad' : 'set_contents_image_ad'
			const prevContentAd = container.querySelector(`.${className}`)
			if (prevContentAd) {
				setContentAd(prevContentAd)
			} else {
				const newDiv = document.createElement('div')
				newDiv.style.display = 'contents'
				newDiv.classList.add(className)
				ele.parentNode?.insertBefore(newDiv, ele.nextSibling)
				setContentAd(newDiv)
			}
		}

		const textElements = Array.from(
			container.querySelectorAll('span:not(.ab_box_article span)'),
		).filter((element) => {
			const text = (element.textContent || '').trim()
			return text.endsWith('.') || text.endsWith('?') || text.endsWith('!')
		})

		if (textElements.length === 0) {
			handleContentAd('image', container.lastChild)
			return
		}

		textElements.forEach((element, index) => {
			/** PC: 3문단 초과일 경우에 3문단 뒤에 표시 (3문단 이하는 표시 X) */
			if (textElements.length > 3 && index === 2) {
				handleContentAd('video', element)
			}

			/** MW: 2문단 초과일 경우에 2문단 뒤에 표시, 2문단 이하일 경우에 마지막문단에 표시 */
			if (
				(textElements.length > 2 && index === 1) ||
				(textElements.length <= 2 && index === textElements.length - 1)
			) {
				handleContentAd('image', element)
			}
		})
	}, [isApp])

	useEffect(() => {
		const container = articleRef.current

		if (container) {
			const elements = container.querySelectorAll('span')
			if (elements?.length) {
				elements.forEach((el) => {
					const styleConvert = el
						?.getAttribute('style')
						?.replaceAll('font-weight: 400;', 'font-weight: 500;')
					// eslint-disable-next-line no-param-reassign
					el.style.cssText = `${styleConvert}`
				})
			}
		}

		/** [광고] 본문 상단 - 비즈보드, EDL 엔터프라이즈 */
		if (container) {
			const abBox = container.querySelector('.ab_box_article')
			const isPhotoBox = !!(abBox?.querySelector('.ab_photo') ?? abBox?.querySelector('img'))
			const video = container.querySelector('.videoPlayerContainer')
			const photo = container.querySelector('.ab_photo') ?? container.querySelector('img')

			const handleAds = (props: Element | null) => {
				if (isApp) {
					setBizboardAd(props)
					return
				}

				setTextAd(props)
			}

			if (isPhotoBox) {
				handleAds(abBox)
			} else if (video) {
				handleAds(video)
			} else if (photo) {
				handleAds(photo)
			}
		}

		if (content) {
			const { FB, instgrm } = window
			if (FB) {
				FB.XFBML.parse()
			}
			if (instgrm) {
				instgrm.Embeds.process()
			}
		}
	}, [isApp, content])

	return (
		content && (
			<Box
				ref={articleRef}
				sx={{
					...ParticleCSS(),
					'& .adtssp_pc': {
						display: { xs: 'none', lg: 'block' },
						marginBottom: { xs: pxToRem(40), lg: pxToRem(48) },
					},
					'& .adtssp_mo': {
						display: { xs: 'block', lg: 'none' },
						marginBottom: { xs: pxToRem(40), lg: pxToRem(48) },
						[theme.mediaQueries.under.origin]: {
							'&>div': {
								width: pxToRem(353),
								height: pxToRem(110),
								overflow: 'hidden',
								'&>iframe': {
									...iframeStyles,
									position: 'static!important',
								},
							},
						},
					},
				}}
				suppressHydrationWarning
			>
				{content}
				{textAd && createPortal(<TextTopAd />, textAd)}
				{contentImageAd && createPortal(<BodyImgMoAd />, contentImageAd)}
				{contentVideoAd && createPortal(<BodyVideoPcAd />, contentVideoAd)}
				{bizboardAd && isApp && createPortal(<BizboardAdFit type="article" />, bizboardAd)}
			</Box>
		)
	)
}

export default Article
